import { addDays, format, isSameDay, isFirstDayOfMonth, isMonday } from "date-fns";
import { useMemo } from "react";
import {
  Bar,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { OneTimeTransaction, RecurringTransaction } from "../types";
import { formatCurrency } from "../lib/utils";

interface BalanceGraphProps {
  currentBalance: number;
  recurringTransactions: RecurringTransaction[];
  oneTimeTransactions: OneTimeTransaction[];
  startDate: Date;
}

interface DataPoint {
  date: Date;
  balance: number;
  transactions: number[];
  dailyChangePositive: number;
  dailyChangeNegative: number;
}

function BalanceGraph({
  currentBalance,
  recurringTransactions,
  oneTimeTransactions,
  startDate,
}: BalanceGraphProps) {
  const data = useMemo(() => {
    const today = startDate;
    const result = [];
    let runningBalance = currentBalance;

    // Add initial balance
    result.push({
      date: today,
      balance: runningBalance,
      transactions: [],
      dailyChangePositive: 0,
      dailyChangeNegative: 0,
    });

    // Calculate balance for next 90 days
    for (let i = 1; i <= 90; i++) {
      const date = addDays(today, i);
      const dailyTransactions: number[] = [];

      // Check recurring transactions
      recurringTransactions.forEach((transaction) => {
        let nextDate = new Date(transaction.startDate);

        while (nextDate <= date) {
          if (isSameDay(nextDate, date)) {
            dailyTransactions.push(transaction.amount);
          }

          switch (transaction.frequency) {
            case "weekly":
              nextDate = addDays(nextDate, 7);
              break;
            case "biweekly":
              nextDate = addDays(nextDate, 14);
              break;
            case "monthly":
              nextDate = addDays(nextDate, 30);
              break;
          }
        }
      });

      // Check one-time transactions
      oneTimeTransactions.forEach((transaction) => {
        if (isSameDay(transaction.date, date)) {
          dailyTransactions.push(transaction.amount);
        }
      });

      const dailyChange = dailyTransactions.reduce(
        (sum, amount) => sum + amount,
        0
      );

      if (dailyTransactions.length > 0) {
        runningBalance += dailyChange;
      }

      result.push({
        date,
        balance: runningBalance,
        transactions: dailyTransactions,
        dailyChangePositive: dailyChange > 0 ? dailyChange : 0,
        dailyChangeNegative: dailyChange < 0 ? dailyChange : 0,
      });
    }

    return result;
  }, [currentBalance, recurringTransactions, oneTimeTransactions, startDate]);

  const formatDate = (date: Date) => format(new Date(date), "MMM d");

  const referenceLines = useMemo(() => {
    const lines = [];
    const today = startDate;
    
    for (let i = 1; i <= 90; i++) {
      const date = addDays(today, i);
      if (isFirstDayOfMonth(date)) {
        lines.push({
          x: date,
          stroke: "hsl(var(--muted-foreground))",
          strokeWidth: 2,
          strokeDasharray: "3 3",
        });
      } else if (isMonday(date)) {
        lines.push({
          x: date,
          stroke: "hsl(var(--muted-foreground))",
          strokeWidth: 1,
          strokeDasharray: "2 2",
        });
      }
    }
    return lines;
  }, [startDate]);

  return (
    <div className="h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
        >
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            interval="preserveStartEnd"
            tick={{ fontSize: 12 }}
          />
          <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 12 }} />
          <ReferenceLine
            y={0}
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="3 3"
          />
          {referenceLines.map((line, index) => (
            <ReferenceLine
              key={index}
              x={line.x.getTime()}
              stroke={line.stroke}
              strokeWidth={line.strokeWidth}
              strokeDasharray={line.strokeDasharray}
              strokeOpacity={0.3}
            />
          ))}
          <Tooltip
            labelFormatter={formatDate}
            formatter={(value: number) => [
              value >= 0 ? formatCurrency(value) : `-${formatCurrency(value)}`,
              "Balance"
            ]}
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              border: "1px solid hsl(var(--border))",
            }}
          />
          <Bar
            dataKey="dailyChangePositive"
            fill="rgb(22 163 74)"
            animationDuration={300}
          />
          <Bar
            dataKey="dailyChangeNegative"
            fill="rgb(220 38 38)"
            animationDuration={300}
          />
          <Line
            type="monotone"
            dataKey="balance"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            dot={false}
            animationDuration={300}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default BalanceGraph;
